import { Archive, ChevronRight, CircleX, Clock, Download, FileJson, FileSpreadsheet, ListFilter, ScrollText, Search, ShieldCheck, TriangleAlert, X } from '@orgus/ui/icons';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useSearchParams } from 'react-router';
import type { AuditEntry, AuditExportFormat, AuditFacet, AuditQuery, ChainVerifyResult } from '@orgus/protocol';
import {
  Button,
  DataTable,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  EmptyState,
  ErrorState,
  IconButton,
  Input,
  Pressable,
  RelativeTime,
  RowStatus,
  Segmented,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  Switch,
  Tooltip,
  formatAbsolute,
  formatInt,
  toUiError,
  toast,
} from '@orgus/ui';
import { PageActions } from '../../components/shell/TopBar.tsx';
import { DateRangePicker, isDateRangePreset, parseDateRange, writeDateRange } from '@orgus/ui';
import { AuditHistory } from '../history/PageHistory.tsx';
import type { Column } from '@orgus/ui';
import { AuditDialog } from '../audit/AuditDialog.tsx';
import { auditActionLabel } from '../audit/labels.ts';
import { useOrgus } from '../../lib/store.ts';
import { useRegisterCommands } from '../../lib/commands.ts';
import type { Command } from '../../lib/commands.ts';
import { FacetPicker } from './FacetPicker.tsx';
import { RetentionDialog } from './RetentionDialog.tsx';
import { VerifyResult } from './VerifyResult.tsx';
import { AuditGlance } from './AuditGlance.tsx';
import { TIME_RANGE_LABEL, auditActorLabel, isTimeRange, machineLabel, rangeFrom } from './model.ts';
import type { TimeRange } from './model.ts';
import { downloadExport, getRetention, useAuditQuery, verifyChain } from './useAuditQuery.ts';

type ResultFilter = 'all' | 'ok' | 'error';

const RANGES = ['24h', '7d', '30d', 'all'] as const;
const rangeText = (r: TimeRange) => (r === 'all' ? 'Any time' : `Last ${TIME_RANGE_LABEL[r]}`);
const RESULT_TEXT: Record<ResultFilter, string> = { all: 'Any result', ok: 'Succeeded', error: 'Failed' };

/** One value per facet, as checkbox items: the desktop pickers' menu, in the phone sheet too. */
function FacetItems({ facets, selected, onChange, label }: { facets: AuditFacet[]; selected: string[]; onChange: (next: string[]) => void; label: (v: string) => string }) {
  const values = [...facets];
  for (const v of selected) if (!values.some((f) => f.value === v)) values.push({ value: v, count: 0 });
  if (!values.length) return <div className="px-2 py-2 text-ui text-fg-subtle">Nothing to choose from yet</div>;
  return (
    <>
      {values.map((f) => (
        <DropdownMenuCheckboxItem
          key={f.value}
          checked={selected.includes(f.value)}
          onSelect={(ev) => ev.preventDefault()}
          onCheckedChange={(on) => onChange(on ? [...selected, f.value] : selected.filter((x) => x !== f.value))}
        >
          <span className="flex min-w-0 flex-1 items-center justify-between gap-4">
            <span className="truncate">{label(f.value)}</span>
            <span className="text-fg-subtle num">{f.count}</span>
          </span>
        </DropdownMenuCheckboxItem>
      ))}
    </>
  );
}

function RangeItems({ value, onChange }: { value: TimeRange; onChange: (r: TimeRange) => void }) {
  return (
    <DropdownMenuRadioGroup value={value} onValueChange={(v) => isTimeRange(v) && onChange(v)}>
      {RANGES.map((r) => (
        <DropdownMenuRadioItem key={r} value={r}>
          {rangeText(r)}
        </DropdownMenuRadioItem>
      ))}
    </DropdownMenuRadioGroup>
  );
}

/** The time range as the same quiet filter button as the facet pickers beside it. */
function RangePicker({ value, onChange }: { value: TimeRange; onChange: (r: TimeRange) => void }) {
  const text = rangeText(value);
  return (
    <span className="flex items-center">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button size="sm" variant={value !== 'all' ? 'secondary' : 'ghost'} icon={<Clock />} aria-label={`Filter by time: ${text}`}>
            {text}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          <DropdownMenuLabel>Written in</DropdownMenuLabel>
          <RangeItems value={value} onChange={onChange} />
        </DropdownMenuContent>
      </DropdownMenu>
      {value !== 'all' && <IconButton label="Clear the time filter" icon={<X />} size="xs" onClick={() => onChange('all')} />}
    </span>
  );
}

/** A row of the phone filter sheet: the filter's name, its value and a chevron, opening its menu. */
export function SheetFilter({ name, value, children }: { name: string; value: string; children: ReactNode }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Pressable className="flex h-row-2line w-full min-w-0 items-center gap-3 px-(--drawer-padding) text-left text-body hover:bg-hover" aria-label={`${name}: ${value}`}>
          <span className="shrink-0 text-fg">{name}</span>
          <span className="min-w-0 flex-1 truncate text-right text-fg-muted">{value}</span>
          <ChevronRight className="size-4 shrink-0 text-fg-subtle" aria-hidden />
        </Pressable>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="max-h-80 overflow-y-auto">
        {children}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * The audit log: every action and every hub change, filterable by who did it (actor), what it
 * touched (object) and what it was (action), searchable across the archive, exportable as CSV
 * or JSON Lines, and checkable end to end with Verify chain. Filters live in the address, so a
 * filtered view is a link.
 */
export function AuditExplorer() {
  const machines = useOrgus((s) => s.machines);
  const [params, setParams] = useSearchParams();
  const [verify, setVerify] = useState<ChainVerifyResult | null>(null);
  const [verifying, setVerifying] = useState(false);
  const [retention, setRetention] = useState(false);
  const [exporting, setExporting] = useState<AuditExportFormat | null>(null);
  const [failure, setFailure] = useState<unknown>(null);
  /** On a phone the filters open in a bottom sheet behind one Filters button; the search box stays out. */
  const [moreFilters, setMoreFilters] = useState(false);

  const list = (k: string) => params.getAll(k).filter(Boolean);
  const resultParam = params.get('result');
  const result: ResultFilter = resultParam === 'ok' || resultParam === 'error' ? resultParam : 'all';
  const rangeParam = params.get('range');
  // The shared date range (ADR 0109) when the URL names one; 24 h from older links; else any time.
  const shared = isDateRangePreset(rangeParam) ? parseDateRange(params, Date.now()) : null;
  const range: TimeRange = shared ? 'all' : isTimeRange(rangeParam) ? rangeParam : 'all';
  const archive = params.get('archive') === '1';
  const actor = list('actor');
  const action = list('action');
  const machine = list('machine');
  const object = params.get('object') ?? '';
  const text = params.get('q') ?? '';
  const entryId = params.get('entry');

  // The time range is fixed when chosen so paging stays stable.
  const [rangeAnchor, setRangeAnchor] = useState(() => Date.now());
  const query: AuditQuery = useMemo(
    () => ({
      ...(actor.length ? { actor } : {}),
      ...(action.length ? { action } : {}),
      ...(machine.length ? { machine } : {}),
      ...(object ? { object } : {}),
      ...(text ? { q: text } : {}),
      ...(result !== 'all' ? { result } : {}),
      ...(range !== 'all' ? { from: rangeFrom(range, rangeAnchor) } : {}),
      ...(shared ? { from: shared.from, ...(shared.preset === 'custom' ? { to: shared.to } : {}) } : {}),
      ...(archive ? { archive: true } : {}),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [params.toString(), rangeAnchor],
  );
  const q = useAuditQuery(query);
  const facets = q.result?.facets ?? { actor: [], action: [], machine: [] };
  const hiddenActive = actor.length + action.length + machine.length + (result !== 'all' ? 1 : 0) + (range !== 'all' || shared ? 1 : 0) + (archive ? 1 : 0);
  const filtered = actor.length + action.length + machine.length > 0 || !!object || !!text || result !== 'all' || range !== 'all' || !!shared;

  const update = useCallback(
    (change: (n: URLSearchParams) => void) =>
      setParams(
        (p) => {
          const n = new URLSearchParams(p);
          change(n);
          return n;
        },
        { replace: true },
      ),
    [setParams],
  );
  const setOne = (k: string, v: string | null) => update((n) => (v == null || v === '' ? n.delete(k) : n.set(k, v)));
  const setList = (k: string, vs: string[]) =>
    update((n) => {
      n.delete(k);
      for (const v of vs) n.append(k, v);
    });
  const clearAll = () =>
    update((n) => {
      for (const k of ['actor', 'action', 'machine', 'object', 'q', 'result', 'range', 'from', 'to']) n.delete(k);
    });

  const setRange = (r: TimeRange) => {
    setRangeAnchor(Date.now());
    setOne('range', r === 'all' ? null : r);
  };

  const runVerify = async () => {
    setVerifying(true);
    setFailure(null);
    try {
      setVerify(await verifyChain());
    } catch (e) {
      setFailure(e);
    } finally {
      setVerifying(false);
    }
  };

  const runExport = async (format: AuditExportFormat) => {
    setExporting(format);
    setFailure(null);
    try {
      await downloadExport(query, format);
      toast(format === 'csv' ? 'CSV export ready' : 'JSON Lines export ready', { tone: 'success', description: `${formatInt(q.result?.total ?? 0)} entries${filtered ? ' matching the filter' : ''}. The export is recorded in this log.` });
    } catch (e) {
      setFailure(e);
    } finally {
      setExporting(null);
    }
  };

  // Retention archives the oldest entries first, so everything older than the live log's oldest
  // entry came from the archive: the table sets those apart under their own group header.
  const [liveFrom, setLiveFrom] = useState<number | null>(null);
  useEffect(() => {
    if (!archive) return;
    let current = true;
    getRetention()
      .then((r) => current && setLiveFrom(r.live.oldest ?? (r.archive.segments.length ? Number.POSITIVE_INFINITY : null)))
      .catch(() => current && setLiveFrom(null));
    return () => {
      current = false;
    };
  }, [archive]);
  const isArchived = (e: AuditEntry) => archive && liveFrom != null && e.at < liveFrom;
  const grouped = archive && liveFrom != null && q.rows.some(isArchived);

  // Row budget: a column whose value is the same on every row says it once, beside the count.
  const oneActor = q.rows.length > 0 && q.rows.every((e) => auditActorLabel(e) === auditActorLabel(q.rows[0]!)) ? auditActorLabel(q.rows[0]!) : null;
  const noRows = !q.loading && q.rows.length === 0;

  const columns = useMemo<Column<AuditEntry>[]>(
    () => {
      const cols: Column<AuditEntry>[] = [
        {
          key: 'result',
          header: 'Result',
          type: 'badge',
          text: (e) => (e.result === 'ok' ? 'succeeded' : 'failed'),
          // Success is the quiet default: its check carries the word as a tooltip; a failure says so.
          cell: (e) => (
            <span className="flex sm:w-16">
              {e.result === 'ok' ? (
                <RowStatus tone="neutral" icon="done" label="Succeeded" hideLabel focusable={false} />
              ) : (
                <>
                  {/* On a phone the word moves to the row's second line, beside the object. */}
                  <RowStatus tone="danger" icon="failed" label="Failed" hideLabel focusable={false} className="sm:hidden" />
                  <RowStatus tone="danger" icon="failed" label="Failed" className="max-sm:hidden" />
                </>
              )}
            </span>
          ),
        },
        {
          key: 'target',
          header: 'Action',
          grow: true,
          text: (e) => `${auditActionLabel(e.action)} ${e.target} ${e.message ?? ''}`,
          // A phone stacks the action over its object, so the object keeps the row's width.
          cell: (e) => (
            <span className="flex min-w-0 flex-col py-1 sm:flex-row sm:items-baseline sm:gap-3 sm:py-0">
              <span className="truncate font-medium text-fg sm:shrink-0">{auditActionLabel(e.action)}</span>
              <span className="flex min-w-0 items-baseline gap-2">
                {e.result === 'error' && <span className="shrink-0 text-caption font-medium text-status-danger sm:hidden">Failed</span>}
                <Tooltip content={e.message ? `${e.target}. ${e.message}` : e.target || 'No object'} delay={500}>
                  <span tabIndex={-1} data-meta="" className="min-w-0 truncate text-caption text-fg-muted sm:text-ui">
                    {e.target || 'No object'}
                  </span>
                </Tooltip>
              </span>
              {e.result === 'error' && e.message && (
                <span data-meta="" className="hidden min-w-0 truncate text-caption text-fg-subtle lg:inline">
                  {e.message}
                </span>
              )}
            </span>
          ),
        },
      ];
      // Fixed widths: the columns stay put when the archive or a filter changes what is listed.
      if (!oneActor) {
        cols.push({
          key: 'actor',
          header: 'By',
          hideBelow: 'lg',
          text: (e) => auditActorLabel(e),
          cell: (e) => <span className="block w-28 truncate text-fg-muted">{auditActorLabel(e)}</span>,
        });
      }
      cols.push(
        {
          key: 'machine',
          header: 'Machine',
          hideBelow: 'md',
          text: (e) => machineLabel(machines, e.machineId),
          cell: (e) => <span className="block w-32 truncate text-fg-muted">{machineLabel(machines, e.machineId)}</span>,
        },
        {
          key: 'at',
          header: 'When',
          type: 'date',
          text: (e) => formatAbsolute(e.at),
          sortValue: (e) => e.at,
          cell: (e) => (
            <Tooltip content={formatAbsolute(e.at)}>
              <span tabIndex={-1} className="block w-16 text-caption text-fg-subtle num">
                <RelativeTime ts={e.at} />
              </span>
            </Tooltip>
          ),
        },
      );
      return cols;
    },
    [machines, oneActor],
  );

  const cmds = useMemo<Command[]>(
    () => [
      { id: 'audit.verify', title: 'Verify the audit chain', group: 'Audit', icon: ShieldCheck, perform: () => void runVerify() },
      { id: 'audit.export.csv', title: 'Export the audit log as CSV', group: 'Audit', icon: FileSpreadsheet, perform: () => void runExport('csv') },
      { id: 'audit.export.jsonl', title: 'Export the audit log as JSON Lines', group: 'Audit', icon: FileJson, perform: () => void runExport('jsonl') },
      { id: 'audit.retention', title: 'Audit retention', group: 'Audit', icon: Archive, perform: () => setRetention(true) },
      { id: 'audit.failed', title: 'Show failed actions', group: 'Audit', icon: CircleX, perform: () => setOne('result', 'error') },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [query],
  );
  useRegisterCommands(cmds, [cmds]);

  const openEntry = entryId ? q.rows.find((e) => e.id === entryId) : undefined;
  const unreadable = q.result?.archive.unreadable ?? [];
  const total = q.result?.total ?? 0;

  return (
    <div className="flex flex-col gap-4">
      <PageActions>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button size="sm" variant="ghost" icon={<Download />} loading={exporting != null} className="hidden sm:inline-flex">
              Export
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>{filtered ? 'Export the entries that match' : 'Export every entry'}</DropdownMenuLabel>
            <DropdownMenuItem icon={<FileSpreadsheet />} onSelect={() => void runExport('csv')}>
              CSV for a spreadsheet
            </DropdownMenuItem>
            <DropdownMenuItem icon={<FileJson />} onSelect={() => void runExport('jsonl')}>
              JSON Lines for a SIEM
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem icon={<Archive />} onSelect={() => setRetention(true)}>
              Retention…
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </PageActions>
      <AuditGlance r={q.result ?? null} verify={verify} verifying={verifying} onVerify={() => void runVerify()} />
      <div className="flex min-w-0 flex-wrap items-center justify-between gap-3">
        <h3 className="text-lead font-semibold text-fg">{shared ? 'Activity in range' : 'Every entry'}</h3>
        <DateRangePicker
          value={shared}
          anyLabel="Any time"
          onChange={(r) =>
            update((n) => {
              const w = writeDateRange(r, n, 'custom');
              if (r.preset !== 'custom') w.set('range', r.preset);
              for (const k of [...n.keys()]) n.delete(k);
              for (const [k, v] of w) n.append(k, v);
            })
          }
        />
      </div>
      {shared && <AuditHistory range={shared} />}

      {failure != null && (
        <div className="rounded-card border border-border bg-surface px-row-x py-2">
          <ErrorState
            variant="inline"
            error={failure}
            action={
              <Button size="sm" variant="ghost" onClick={() => setFailure(null)}>
                Dismiss
              </Button>
            }
          />
        </div>
      )}
      {verify && <VerifyResult r={verify} onDismiss={() => setVerify(null)} />}
      {unreadable.length > 0 && (
        <div role="status" className="flex items-start gap-3 rounded-card border border-status-attention-line bg-status-attention-soft px-row-x py-2 text-ui">
          <TriangleAlert className="mt-0.5 size-4 shrink-0 text-status-attention" aria-hidden />
          <p className="text-fg">
            <span className="font-medium text-status-attention-ink">Some archived entries are missing from these results.</span>{' '}
            <span className="text-fg-muted">
              {unreadable.length === 1 ? `${unreadable[0]} could not be read` : `${unreadable.length} archived segments could not be read`}: it was changed or deleted. Run Verify chain to see where.
            </span>
          </p>
        </div>
      )}

      <DataTable
        label="Audit log"
        // An empty log has no rows to line the headers up with: the header band would read as an empty row.
        className={noRows ? '[&_thead]:hidden' : undefined}
        rows={q.rows}
        columns={columns}
        rowKey={(e) => e.id}
        initialSort={{ key: 'at', dir: 'desc' }}
        pageSize={false}
        loading={q.loading && !q.result}
        error={q.result ? undefined : q.error ?? undefined}
        onRetry={q.refresh}
        filters={
          <>
            <Input
              size="sm"
              type="search"
              icon={<Search />}
              aria-label="Search actions"
              placeholder="Search actions"
              value={text}
              onChange={(ev) => setOne('q', ev.target.value)}
              wrapperClassName="w-full sm:w-48"
            />
            {/* A phone folds every filter into one bottom sheet, its count on the button. */}
            <Button size="sm" variant={hiddenActive ? 'secondary' : 'ghost'} icon={<ListFilter />} aria-haspopup="dialog" onClick={() => setMoreFilters(true)} className="sm:hidden">
              {hiddenActive ? `Filters ${formatInt(hiddenActive)}` : 'Filters'}
            </Button>
            <div className="contents max-sm:hidden">
              <FacetPicker name="Action" heading="Show these actions" facets={facets.action} selected={action} onChange={(v) => setList('action', v)} label={auditActionLabel} />
              <FacetPicker name="Actor" heading="Done by" facets={facets.actor} selected={actor} onChange={(v) => setList('actor', v)} label={(v) => v} />
              <FacetPicker name="Machine" heading="On these machines" facets={facets.machine} selected={machine} onChange={(v) => setList('machine', v)} label={(v) => machineLabel(machines, v)} />
              <Segmented
                label="Result"
                value={result}
                onChange={(r) => setOne('result', r === 'all' ? null : r)}
                options={[
                  { value: 'all', label: 'All' },
                  { value: 'ok', label: 'Succeeded' },
                  { value: 'error', label: 'Failed' },
                ]}
              />
              {!shared && range !== 'all' && <RangePicker value={range} onChange={setRange} />}
              <Button size="sm" variant={archive ? 'secondary' : 'ghost'} icon={<Archive />} aria-pressed={archive} onClick={() => setOne('archive', archive ? null : '1')}>
                {archive ? 'Archive included' : 'Include archive'}
              </Button>
            </div>
            {object && (
              <Button size="sm" variant="secondary" icon={<X />} onClick={() => setOne('object', null)} aria-label={`Clear the object filter: ${object}`}>
                Object: {object}
              </Button>
            )}
            {filtered && (
              <Button size="sm" variant="ghost" icon={<X />} onClick={clearAll} className="max-sm:hidden">
                Clear filters
              </Button>
            )}
          </>
        }
        toolbarEnd={
          <span aria-live="polite" className="num">
            {q.result
              ? `${formatInt(total)} ${total === 1 ? 'entry' : 'entries'}${q.result.archive.searched ? `, ${formatInt(q.result.archive.entries)} archived` : ''}${oneActor && total > 1 ? `, all by ${oneActor}` : ''}`
              : ''}
          </span>
        }
        groupBy={grouped ? (e) => (isArchived(e) ? 'archive' : 'live') : undefined}
        renderGroup={(key, rows) => (
          <span className="flex items-center gap-2 text-caption">
            {key === 'archive' && <Archive className="size-4 text-fg-subtle" aria-hidden />}
            <span className="font-medium text-fg-muted">{key === 'archive' ? 'Archive' : 'Live log'}</span>
            <span className="text-fg-subtle num">{formatInt(rows.length)}</span>
          </span>
        )}
        onRowClick={(e) => setOne('entry', e.id)}
        rowLabel={(e) => `${auditActionLabel(e.action)}: ${e.target}, by ${auditActorLabel(e)}, ${e.result === 'ok' ? 'succeeded' : 'failed'}. Open details`}
        activeKey={entryId}
        loadMore={{ onLoad: q.loadMore, loading: q.loadingMore, more: !!q.result?.more }}
        empty={
          filtered ? (
            <EmptyState
              icon={<Search />}
              title="No entries match"
              action={
                archive ? undefined : (
                  <Button size="sm" variant="secondary" icon={<Archive />} onClick={() => setOne('archive', '1')}>
                    Include archive
                  </Button>
                )
              }
            />
          ) : (
            <EmptyState
              icon={<ScrollText />}
              title="No actions yet"
              description="Sessions, commands, pipeline changes and exports made through Orgus show up here."
            />
          )
        }
      />
      {q.error != null && q.result != null && (
        <p role="status" className="text-caption text-status-danger">
          Live updates paused: {toUiError(q.error).what} The list shows what loaded last.
        </p>
      )}
      <Sheet open={moreFilters} onOpenChange={setMoreFilters}>
        <SheetContent side="bottom">
          <SheetHeader>
            <SheetTitle>Filters</SheetTitle>
            <SheetDescription className="sr-only">Narrow the audit log</SheetDescription>
          </SheetHeader>
          <div className="flex flex-col py-2">
            <SheetFilter name="Action" value={action.length === 0 ? 'Any action' : action.length === 1 ? auditActionLabel(action[0]!) : `${formatInt(action.length)} actions`}>
              <FacetItems facets={facets.action} selected={action} onChange={(v) => setList('action', v)} label={auditActionLabel} />
            </SheetFilter>
            <SheetFilter name="Actor" value={actor.length === 0 ? 'Anyone' : actor.length === 1 ? actor[0]! : `${formatInt(actor.length)} actors`}>
              <FacetItems facets={facets.actor} selected={actor} onChange={(v) => setList('actor', v)} label={(v) => v} />
            </SheetFilter>
            <SheetFilter name="Machine" value={machine.length === 0 ? 'Any machine' : machine.length === 1 ? machineLabel(machines, machine[0]!) : `${formatInt(machine.length)} machines`}>
              <FacetItems facets={facets.machine} selected={machine} onChange={(v) => setList('machine', v)} label={(v) => machineLabel(machines, v)} />
            </SheetFilter>
            <SheetFilter name="Result" value={RESULT_TEXT[result]}>
              <DropdownMenuRadioGroup value={result} onValueChange={(r) => setOne('result', r === 'all' ? null : r)}>
                {(['all', 'ok', 'error'] as const).map((r) => (
                  <DropdownMenuRadioItem key={r} value={r}>
                    {RESULT_TEXT[r]}
                  </DropdownMenuRadioItem>
                ))}
              </DropdownMenuRadioGroup>
            </SheetFilter>
            <SheetFilter name="Time" value={rangeText(range)}>
              <RangeItems value={range} onChange={setRange} />
            </SheetFilter>
            <label className="flex h-row-2line w-full items-center gap-3 px-(--drawer-padding) text-body">
              <span className="min-w-0 flex-1 text-fg">Include archive</span>
              <Switch checked={archive} onCheckedChange={(v) => setOne('archive', v === true ? '1' : null)} aria-label="Include archive" />
            </label>
          </div>
          <SheetFooter>
            {hiddenActive > 0 && (
              <Button variant="ghost" onClick={clearAll}>
                Clear filters
              </Button>
            )}
            <Button variant="primary" onClick={() => setMoreFilters(false)}>
              {q.result ? `Show ${formatInt(total)} ${total === 1 ? 'entry' : 'entries'}` : 'Show entries'}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
      <AuditDialog entry={openEntry} onClose={() => setOne('entry', null)} />
      <RetentionDialog open={retention} onOpenChange={setRetention} />
    </div>
  );
}
